import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import Card from '../../components/Card/Card';
import Layout, { OrderRequestResult } from '../../components/Layout/Layout';
import { useLanguage } from '../../locale/useLanguage';
import { Routes } from '../../routes/types';
import './ChangeAddressView.css';
import newAddressImage from '../../assets/images/new-address.png';
import favoriteAddressImage from '../../assets/images/favorite-address-list.png';
import { useBack } from '../../hooks/useBack';
import { useAppDispatch } from '../../redux/core';
import { setupNewAddressLightSignInOperation } from '../../helpers/auth';
import { Button } from '../../components/Button/Button';
import { useIsEditDoorbellPositionEnabled } from '../../hooks/mlkActions/useIsEditDoorbellPositionEnabled';
import { useIsEditNotesEnabled } from '../../hooks/mlkActions/useIsEditNotesEnabled';
import { useIsEditAddressEnabled } from '../../hooks/mlkActions/useIsEditAddressEnabled';
import { isNil } from 'lodash';
import { CalculatedOrderStatus, FaqFeature, OrderState } from '../../models';
import { ConfirmationModal } from '../../components/ConfirmationModal/ConfirmationModal';
import { useCalculatedOrderStatus } from '../../hooks/validations/useCalculatedOrderStatus';
import { ModalRef } from '../../components/Modal/Modal';

type ChangeAddressViewProps = {};

export const ChangeAddressView: React.FC<ChangeAddressViewProps> = () => {
  const [t] = useLanguage();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const goBack = useBack();
  const [orderRequestResult, setOrderRequestResult] =
    useState<OrderRequestResult>({ isLoading: true, isFetching: false });

  const calculatedStatus = useCalculatedOrderStatus();

  const isEditDoorbellPositionEnabled = useIsEditDoorbellPositionEnabled();
  const isEditNotesEnabled = useIsEditNotesEnabled();
  const isAddressChangeEnabled = useIsEditAddressEnabled();
  const { data: order } = orderRequestResult;

  const [confirmationModalProps, setConfirmationModalProps] = useState({
    title: '',
    description: '',
    confirmButtonText: '',
    onConfirm: () => {},
    onCancel: () => {},
  });
  const confirmationModalRef = useRef<ModalRef>(null);

  useEffect(() => {
    if (
      !isNil(isAddressChangeEnabled) &&
      !isNil(isEditDoorbellPositionEnabled) &&
      !isNil(isEditNotesEnabled) &&
      !isAddressChangeEnabled &&
      !isEditDoorbellPositionEnabled &&
      !isEditNotesEnabled
    ) {
      history.replace(Routes.Home);
    }
  }, [
    history,
    isAddressChangeEnabled,
    isEditDoorbellPositionEnabled,
    isEditNotesEnabled,
  ]);

  const navigateToNewAddress = useCallback(() => {
    if (isAddressChangeEnabled) {
      if (
        calculatedStatus === CalculatedOrderStatus.InTransit ||
        order?.status === OrderState.Baseline
      ) {
        setConfirmationModalProps({
          ...confirmationModalProps,
          title: t('changeAddress.editAddressWarningModalTitle'),
          description: t('changeAddress.editAddressWarningModalDescription1'),
          confirmButtonText: t(
            'changeAddress.editAddressWarningModalEditAddress'
          ),
          onConfirm: () => {
            setupNewAddressLightSignInOperation(dispatch);
            history.push(Routes.CreateAddress, { isFromChangeAddress: true });
          },
          onCancel: () => {},
        });
        confirmationModalRef.current?.openModal();
      } else {
        setupNewAddressLightSignInOperation(dispatch);
        history.push(Routes.CreateAddress, { isFromChangeAddress: true });
      }
    }
  }, [
    dispatch,
    history,
    isAddressChangeEnabled,
    confirmationModalProps,
    t,
    calculatedStatus,
    order?.status,
  ]);

  const navigateToFavoriteAddresses = useCallback(() => {
    if (isAddressChangeEnabled) {
      if (
        calculatedStatus === CalculatedOrderStatus.InTransit ||
        order?.status === OrderState.Baseline
      ) {
        setConfirmationModalProps({
          ...confirmationModalProps,
          title: t('changeAddress.editAddressWarningModalTitle'),
          description: t('changeAddress.editAddressWarningModalDescription1'),
          confirmButtonText: t(
            'changeAddress.editAddressWarningModalEditAddress'
          ),
          onConfirm: () => {
            history.push(Routes.ChooseFavoriteAddress);
          },
          onCancel: () => {},
        });
        confirmationModalRef.current?.openModal();
      } else {
        history.push(Routes.ChooseFavoriteAddress);
      }
    }
  }, [
    history,
    isAddressChangeEnabled,
    confirmationModalProps,
    t,
    calculatedStatus,
    order?.status,
  ]);

  const navigateToEditAddressNotes = useCallback(() => {
    if (isEditNotesEnabled) {
      history.push(Routes.EditAddressNotes);
    }
  }, [history, isEditNotesEnabled]);

  const navigateToEditAddressDoorbellPosition = useCallback(() => {
    if (isEditDoorbellPositionEnabled) {
      history.push(Routes.EditAddressDoorbellPosition);
    }
  }, [history, isEditDoorbellPositionEnabled]);

  const goToInfo = useCallback(() => {
    history.push(Routes.Faq.replace(':feature', FaqFeature.Address));
  }, [history]);

  return (
    <Layout
      title={t('pageTitles.changeAddress')}
      hasBack
      isGreyBg
      hasInfo
      onInfoClick={goToInfo}
      onBackClick={goBack}
      onOrderChange={setOrderRequestResult}
    >
      <ConfirmationModal
        ref={confirmationModalRef}
        title={confirmationModalProps.title}
        description={confirmationModalProps.description}
        onConfirm={confirmationModalProps.onConfirm}
        onCancel={confirmationModalProps.onCancel}
        confirmButtonText={confirmationModalProps.confirmButtonText}
      />
      <section>
        <div className="change-address--card-link">
          <div
            className={
              !isAddressChangeEnabled ? 'change-address--card-disabled' : ''
            }
          >
            <a
              role="link"
              onClick={navigateToNewAddress}
              aria-label="New address"
            >
              <Card>
                <div className="change-address--card-body">
                  <img
                    className="change-address--card-image"
                    src={newAddressImage}
                    alt="Route signal"
                  />
                  <div className="change-address--card-text-container">
                    <h2 className="change-address--subtitle">
                      {t('changeAddress.newAddress')}
                    </h2>
                    <p className="change-address--card-description">
                      {t('changeAddress.newAddressDescription')}
                    </p>
                  </div>
                </div>
              </Card>
            </a>
          </div>
        </div>
        <div className="change-address--card-link">
          <div
            className={
              !isAddressChangeEnabled ? 'change-address--card-disabled' : ''
            }
          >
            <a
              role="link"
              aria-label="Favorite addresses"
              onClick={navigateToFavoriteAddresses}
            >
              <Card>
                <div className="change-address--card-body">
                  <img
                    className="change-address--card-image"
                    src={favoriteAddressImage}
                    alt="Route signal"
                  />
                  <div className="change-address--card-text-container">
                    <h2 className="change-address--subtitle">
                      {t('changeAddress.favoriteAdressesList')}
                    </h2>
                    <p className="change-address--card-description">
                      {t('changeAddress.favoriteAdressesListDescription')}
                    </p>
                  </div>
                </div>
              </Card>
            </a>
          </div>
        </div>
      </section>
      <section className="change-address--address-container">
        <h2 className="change-address--title">
          {t('changeAddress.currentAddress')}
        </h2>
        <p className="change-address--address">
          {order?.deliveryAddress.address}
        </p>
      </section>
      <div>
        <Button
          isDisabled={!isEditDoorbellPositionEnabled}
          onClick={navigateToEditAddressDoorbellPosition}
          isPrimary
          label={t('changeAddress.changeAccessPoint')}
        />
        <div className="change-address--space-button">
          <Button
            isDisabled={!isEditNotesEnabled}
            onClick={navigateToEditAddressNotes}
            isWhite
            label={t('changeAddress.helpDriver')}
          />
        </div>
      </div>
    </Layout>
  );
};
